/**
 * FlexShareModal ✦
 *
 * Wraps the FlexCard in a glass overlay with:
 *   • live preview of the 9:16 share artwork
 *   • Save Image · Native Share · Copy to Clipboard fallback
 *   • caption suggestions (4 pre-baked, mood-matched to kind)
 *   • inspiration line "You're inspiring future Crew members ✦"
 *     once a share or save lands successfully
 *
 * Performance lock: FlexCard renders OFF-screen at full 540×960
 * so html-to-image captures it crisp; the preview shrinks via
 * CSS transform — no extra rasterization step on the device.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toPng, toBlob } from 'html-to-image';
import { Download, Share, Copy, X as XIcon, Check } from '@phosphor-icons/react';
import FlexCard from './FlexCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const CAPTIONS_FOR = (kind, payload) => {
  if (kind === 'badge') {
    return [
      `Just unlocked ${payload.label} 🚀`,
      'Building confidence one event at a time.',
      'Started with zero experience.',
      'Every shift feels different. This one hit ✦',
    ];
  }
  if (kind === 'level') {
    return [
      `Level ${payload.level} crew member ✦`,
      'On a roll — one shift at a time 🚀',
      'Building it, one event at a time.',
      'Confidence levelling up.',
    ];
  }
  if (kind === 'first') {
    return [
      'First event complete ✦',
      'Showed up. Stayed nervous. Loved it.',
      'Started with zero experience.',
      'And we’re off ✦',
    ];
  }
  return [
    'New reference unlocked ✦',
    'Suppliers vouched. That hits different.',
    'Building trust, one night at a time.',
    'Just earned it ✦',
  ];
};

const FlexShareModal = ({ open, onClose, kind = 'badge', payload = {} }) => {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [dataUrl, setDataUrl] = useState('');
  const [done, setDone] = useState(''); // 'saved' | 'shared' | 'copied' | 'caption'
  const [error, setError] = useState('');
  const [captionIdx, setCaptionIdx] = useState(0);
  const captions = CAPTIONS_FOR(kind, payload);

  useEffect(() => {
    if (!open) return;
    setDone(''); setError(''); setDataUrl('');
    // Defer generation one frame so layout settles. Re-render
    // with the off-screen card mounted, then render to PNG.
    const t = setTimeout(async () => {
      if (!cardRef.current) return;
      setGenerating(true);
      try {
        const url = await toPng(cardRef.current, {
          pixelRatio: 2,
          cacheBust: true,
          backgroundColor: '#06061A',
        });
        setDataUrl(url);
      } catch (err) {
        setError("Couldn't render the card — try again");
      } finally {
        setGenerating(false);
      }
    }, 60);
    return () => clearTimeout(t);
  }, [open, kind, payload?.id, payload?.level]);

  // Escape key dismiss — matches BadgeUnlockOverlay's contract.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const trackShare = async (channel) => {
    // Fire-and-forget — never block the social share on this
    try {
      await axios.post(`${API}/crew/me/flex-share`, { kind, badge_id: payload.id || null, channel });
    } catch { /* silent */ }
  };

  const onSave = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `eventdrop-crew-${payload.id || kind}.png`;
    a.click();
    setDone('saved');
    trackShare('save');
  };

  const onShare = async () => {
    if (!cardRef.current) return;
    try {
      const blob = await toBlob(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: '#06061A' });
      if (!blob) throw new Error('blob null');
      const file = new File([blob], `eventdrop-crew-${payload.id || kind}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'EventDrop Crew ✦',
          text: captions[captionIdx],
        });
        setDone('shared');
        trackShare('native_share');
        return;
      }
      // No native share — fall back to copy
      await navigator.clipboard.write([
        new window.ClipboardItem({ 'image/png': blob }),
      ]);
      setDone('copied');
      trackShare('clipboard');
    } catch (err) {
      // Last resort — copy the data URL link
      try {
        await navigator.clipboard.writeText(dataUrl);
        setDone('copied');
        trackShare('clipboard_link');
      } catch {
        setError('Sharing not supported — try Save Image instead');
      }
    }
  };

  const onCopyCaption = async () => {
    try {
      await navigator.clipboard.writeText(captions[captionIdx]);
      setDone('caption');
    } catch {
      setError("Couldn't copy caption");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="flex-share-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(7, 7, 26, 0.86)',
        backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, overflowY: 'auto',
        fontFamily: FONT_STACK, color: TEXT,
      }}
    >
      {/* Hidden off-screen FlexCard — full native size so the
          PNG export stays Instagram-Story crisp. */}
      <div
        aria-hidden
        style={{
          position: 'fixed', left: -10000, top: 0,
          pointerEvents: 'none',
        }}
      >
        <FlexCard ref={cardRef} kind={kind} payload={payload} />
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460,
          maxHeight: 'calc(100vh - 40px)',
          overflowY: 'auto',
          background: 'rgba(15,15,32,0.92)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 22,
          padding: 'clamp(18px, 4vw, 28px)',
          position: 'relative',
          boxShadow: `0 30px 70px rgba(0,0,0,0.55), 0 0 60px ${PURPLE}33`,
        }}
      >
        <button
          onClick={onClose}
          data-testid="flex-share-close"
          style={{
            position: 'absolute', top: 14, right: 14,
            background: 'rgba(255,255,255,0.08)', border: 'none',
            width: 32, height: 32, borderRadius: '50%',
            color: TEXT, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="Close"
        >
          <XIcon size={14} weight="bold" />
        </button>

        <p style={{
          margin: '0 0 4px', fontSize: 11, fontWeight: 800,
          letterSpacing: '0.32em', textTransform: 'uppercase',
          background: `linear-gradient(135deg, ${BLUE}, ${PINK})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          ✦ Flex it
        </p>
        <h2 style={{ margin: '0 0 18px', fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em' }}>
          Share your moment
        </h2>

        {/* Preview frame */}
        <div
          data-testid="flex-share-preview"
          style={{
            width: '100%', aspectRatio: '9 / 16',
            borderRadius: 14, overflow: 'hidden',
            background: '#06061A',
            border: `1px solid rgba(255,255,255,0.08)`,
            position: 'relative',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          {dataUrl ? (
            <img
              alt="Share preview"
              src={dataUrl}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              data-testid="flex-share-preview-img"
            />
          ) : (
            <p style={{ color: TEXT_MUTED, fontSize: 13 }}>
              {generating ? 'Rendering…' : 'Preparing your card ✦'}
            </p>
          )}
        </div>

        {/* Caption picker */}
        <p style={{
          margin: '0 0 6px', fontSize: 11, fontWeight: 700, letterSpacing: '0.24em',
          textTransform: 'uppercase', color: TEXT_MUTED,
        }}>
          Caption
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {captions.map((c, i) => (
            <button
              key={i}
              onClick={() => setCaptionIdx(i)}
              data-testid={`flex-caption-${i}`}
              data-selected={captionIdx === i ? '1' : '0'}
              style={{
                padding: '7px 11px', borderRadius: 999,
                border: `1px solid ${captionIdx === i ? PINK : 'rgba(255,255,255,0.1)'}`,
                background: captionIdx === i
                  ? `linear-gradient(135deg, ${PURPLE}33, ${PINK}33)`
                  : 'rgba(255,255,255,0.04)',
                color: captionIdx === i ? TEXT : TEXT_MUTED,
                cursor: 'pointer', fontFamily: FONT_STACK,
                fontSize: 11.5, fontWeight: 700,
                maxWidth: '100%',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {c.length > 28 ? c.slice(0, 28) + '…' : c}
            </button>
          ))}
        </div>
        <button
          onClick={onCopyCaption}
          data-testid="flex-caption-copy"
          style={{
            background: 'transparent', border: 'none', color: BLUE,
            cursor: 'pointer', fontFamily: FONT_STACK,
            fontSize: 12, fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: 0, marginBottom: 18,
          }}
        >
          <Copy size={12} weight="bold" /> Copy caption
        </button>

        {/* Action row */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={onShare}
            disabled={!dataUrl || generating}
            data-testid="flex-share-native"
            style={{
              flex: 1, minWidth: 140, padding: '13px 18px', borderRadius: 999,
              border: 'none', cursor: !dataUrl ? 'wait' : 'pointer', color: TEXT,
              fontFamily: FONT_STACK, fontSize: 13.5, fontWeight: 900,
              background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
              boxShadow: `0 10px 26px ${PURPLE}55`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: !dataUrl ? 0.7 : 1,
            }}
          >
            <Share size={14} weight="fill" /> Share
          </button>
          <button
            onClick={onSave}
            disabled={!dataUrl || generating}
            data-testid="flex-share-save"
            style={{
              flex: 1, minWidth: 140, padding: '13px 18px', borderRadius: 999,
              background: 'rgba(255,255,255,0.06)',
              border: `1px solid ${BLUE}55`, color: TEXT,
              cursor: !dataUrl ? 'wait' : 'pointer', fontFamily: FONT_STACK,
              fontSize: 13.5, fontWeight: 800,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: !dataUrl ? 0.7 : 1,
            }}
          >
            <Download size={14} weight="bold" /> Save image
          </button>
        </div>

        {error && (
          <p data-testid="flex-share-error" style={{
            color: PINK, fontSize: 12.5, marginTop: 12, marginBottom: 0,
          }}>{error}</p>
        )}

        {done && (
          <p
            data-testid="flex-share-success"
            data-state={done}
            style={{
              marginTop: 14, marginBottom: 0,
              padding: '12px 14px', borderRadius: 12,
              background: `linear-gradient(135deg, ${PURPLE}1F, ${BLUE}1F)`,
              border: `1px solid ${BLUE}55`,
              color: TEXT, fontSize: 13, fontWeight: 700, lineHeight: 1.45,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <Check size={14} weight="bold" color={BLUE} />
            {done === 'caption'
              ? 'Caption copied — paste it on your story ✦'
              : "You're inspiring future Crew members ✦"}
          </p>
        )}
      </div>
    </div>
  );
};

export default FlexShareModal;
